import ContentContainer from "Components/Container/ContentContainer";
import { Button, SubTitle } from "Components/CustomComponents";
import { useLazyGetDriversQuery } from "Data/services/driversApi";
import React, { useEffect, useState } from "react";
import { Col, Row, Spinner } from "react-bootstrap";
import { RiSearch2Line } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import { WEB_ROUTES } from "../../constants";
import DriverProfileCard from "./component/DriverProfileCard";
import styles from "./styles/Driver.module.scss";
import CustomLoader from "Components/CustomComponents/CustomLoader";
import NoDataFound from "Components/CustomComponents/NoDataFound";
import CustomInfiniteScroll from "Components/CustomInfiniteScroll";
import { Drivers } from "Data/interfaces";

const DriversScreen: React.FC = () => {
  const navigate = useNavigate();
  // Pagination State
  const [drivers, setDrivers] = useState<Drivers[]>([]);
  const [page, setPage] = useState<number>(1);
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [search, setSearch] = useState<string>("");
  const [filteredDrivers, setFilteredDrivers] = useState<Drivers[]>([]);

  // Effect to filter drivers locally
  useEffect(() => {
    if (search.trim() === "") {
      setFilteredDrivers(drivers);
    } else {
      setFilteredDrivers(
        drivers.filter((driver) =>
          driver.username.toLowerCase().includes(search.toLowerCase())
        )
      );
    }
  }, [search, drivers]);

  const [getDrivers, { isFetching }] = useLazyGetDriversQuery();

  // Fetch Routes on Date Change
  useEffect(() => {
    setPage(1);
    setDrivers([]);
    getDrivers({ page: 1 }).then(({ data }) => {
      setDrivers(data?.data || []);
      setHasMore(data?.links.total !== data?.links.current);
    });
  }, []);

  // Fetch More Routes for Infinite Scroll
  const fetchMoreRoutes = async () => {
    if (!hasMore || isFetching) return;

    const nextPage = page + 1;
    try {
      const response = await getDrivers({ page: nextPage });
      const data = response?.data; // Ensure data is accessed safely

      if (data !== undefined && data?.data?.length > 0) {
        setDrivers((prev) => [...prev, ...data.data]);
        setPage(nextPage);
        setHasMore(data.links.total !== data.links.current); // Ensure `hasMore` is boolean
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error("Error fetching more driver:", error);
    }
  };

  return (
    <ContentContainer
      classes={`${styles.Drivers} `}
      hasBoxShadow={true}
      style={{ minWidth: "280px" }}
    >
      <SubTitle text="Drivers" classes="mb-4" />
      <Row className="d-flex justify-content-between gap-3 mb-4">
        <Col sm={12} md={6}>
          <div className={styles.search}>
            <div className={styles.searchButton}>
              <RiSearch2Line />
            </div>
            <input
              type="search"
              placeholder="Search "
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        </Col>

        <Col sm={12} md={3}>
          <Button
            text={"Add New Driver"}
            className=" blackBg"
            onClick={() => navigate(WEB_ROUTES.ADD_DRIVERS)}
          />
        </Col>
      </Row>
      {/* <section className='my-3 d-flex gap-4 justify-content-start flex-wrap'> */}
      <Row className="">
        {isFetching && drivers.length === 0 ? (
          <CustomLoader />
        ) : drivers?.length === 0 ? (
          <NoDataFound />
        ) : (
          <CustomInfiniteScroll
            loader={
              <div className="flex items-center justify-center">
                <Spinner animation="border" />
              </div>
            }
            getRecords={fetchMoreRoutes}
            hasMore={hasMore}
          >
            {filteredDrivers.map((driver, index) => {
              return (
                <Col
                  key={index}
                  sm={12}
                  md={6}
                  lg={4}
                  xl={3}
                  className="my-2"
                  style={{ cursor: "pointer" }}
                >
                  <div
                    onClick={() =>
                      navigate(WEB_ROUTES.DRIVER_PROFILE(driver.id), {
                        state: { driver },
                      })
                    }
                  >
                    <DriverProfileCard driver={driver} />
                  </div>
                </Col>
              );
            })}
          </CustomInfiniteScroll>
        )}
      </Row>

      {/* </section> */}
    </ContentContainer>
  );
};

export default DriversScreen;
